import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import GuestBookForm, { type GuestEntry } from "./components/GuestBookForm";
import GuestBookThankYou from "./components/GuestBookThankYou";
import { getExpiryStatus } from "@/lib/signatureBookExpiry";

/* ─── Types ─────────────────────────────────────────── */
interface SignatureBook {
  id: string;
  tenant_id: string;
  title: string;
  status: string;
  guest_book_enabled: boolean;
  cover_photo_url: string | null;
  closing_message: string | null;
  activated_at: string | null;
  expires_at: string | null;
  event_date: string | null;
}

interface Photo {
  id: string;
  photo_url: string;
  sort_order: number;
}

type PageState = "loading" | "open" | "form" | "thanks" | "error";

const ACCENT = "#C8A96E";

/* ─── Loading screen ─────────────────────────────────── */
function GuestBookLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#FAF8F4" }}>
      <div className="text-center space-y-4">
        <div
          className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin mx-auto"
          style={{ borderColor: ACCENT, borderTopColor: "transparent" }}
        />
        <p className="text-sm tracking-widest uppercase" style={{ color: ACCENT }}>Cargando libro…</p>
      </div>
    </div>
  );
}

/* ─── Error screen ───────────────────────────────────── */
function GuestBookError({ message }: { message: string }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center px-6"
      style={{ backgroundColor: "#FAF8F4" }}
    >
      <div className="text-center max-w-sm space-y-5">
        <div
          className="w-20 h-20 mx-auto flex items-center justify-center rounded-full"
          style={{ backgroundColor: ACCENT + "15" }}
        >
          <i className="ri-book-2-line text-4xl" style={{ color: ACCENT + "60" }}></i>
        </div>
        <div className="space-y-2">
          <h1
            className="text-slate-800 text-2xl font-bold"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Libro no disponible
          </h1>
          <p className="text-slate-400 text-sm leading-relaxed">{message}</p>
        </div>
      </div>
    </div>
  );
}

/* ─── Cover/open screen ──────────────────────────────── */
function GuestBookCover({
  book,
  onEnter,
}: {
  book: SignatureBook;
  onEnter: () => void;
}) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-end pb-14 sm:justify-center sm:pb-0 px-6 relative"
      style={{
        backgroundColor: "#1C1008",
        backgroundImage: book.cover_photo_url
          ? `url(${book.cover_photo_url})`
          : "none",
        backgroundSize: "cover",
        backgroundPosition: "center top",
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70" />

      {/* Content */}
      <div className="relative text-center max-w-sm w-full space-y-7">
        {/* Icon */}
        <div
          className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl"
          style={{ backgroundColor: ACCENT + "30", backdropFilter: "blur(12px)", border: `1px solid ${ACCENT}40` }}
        >
          <i className="ri-book-open-line text-3xl" style={{ color: ACCENT }}></i>
        </div>

        {/* Title */}
        <div className="space-y-2">
          <p
            className="text-xs uppercase tracking-[0.4em] font-semibold"
            style={{ color: "rgba(255,255,255,0.55)" }}
          >
            Libro de Firmas
          </p>
          <h1
            className="text-3xl font-bold leading-tight text-white"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              textShadow: "0 2px 16px rgba(0,0,0,0.5)",
            }}
          >
            {book.title}
          </h1>
          {book.event_date && (
            <p className="text-sm" style={{ color: "rgba(255,255,255,0.65)" }}>
              {new Date(book.event_date).toLocaleDateString("es-MX", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}
            </p>
          )}
        </div>

        {/* CTA */}
        <button
          onClick={onEnter}
          className="w-full py-4 rounded-2xl font-bold text-sm cursor-pointer flex items-center justify-center gap-2 whitespace-nowrap active:scale-95 transition-all"
          style={{
            backgroundColor: ACCENT,
            color: "#1C1008",
            boxShadow: `0 8px 24px ${ACCENT}40`,
          }}
        >
          <i className="ri-pencil-line"></i>
          Dejar mi mensaje
        </button>
        <p className="text-xs" style={{ color: "rgba(255,255,255,0.35)" }}>
          Tu mensaje quedará guardado en el libro
        </p>
      </div>
    </div>
  );
}

/* ─── Main Page ──────────────────────────────────────── */
export default function GuestBookPage() {
  const { slug } = useParams<{ slug: string }>();

  const [state,       setState]     = useState<PageState>("loading");
  const [errorMsg,    setErrorMsg]  = useState<string>("");
  const [book,        setBook]      = useState<SignatureBook | null>(null);
  const [photos,      setPhotos]    = useState<Photo[]>([]);
  const [lastGuestName, setLastGuestName] = useState("");

  useEffect(() => {
    if (!slug) { setErrorMsg("Link inválido."); setState("error"); return; }
    const load = async () => {
      const { data, error } = await supabase
        .from("signature_books")
        .select("id, tenant_id, title, status, guest_book_enabled, cover_photo_url, closing_message, activated_at, expires_at, event_date")
        .eq("public_slug", slug)
        .maybeSingle();

      if (error || !data) { setErrorMsg("Este libro no existe o el link es incorrecto."); setState("error"); return; }
      const b = data as SignatureBook;

      if (b.status !== "published") { setErrorMsg("Este libro aún no está disponible al público. Vuelve más tarde."); setState("error"); return; }
      if (!b.guest_book_enabled)     { setErrorMsg("El libro de visitas no está habilitado en este momento."); setState("error"); return; }

      // Expiry check via shared utility
      const expStatus = getExpiryStatus(b.expires_at);
      if (expStatus === "expired") {
        setErrorMsg("Este libro de firmas ya cerró. Gracias por haber sido parte del evento.");
        setState("error");
        return;
      }

      setBook(b);

      // Load photos
      const { data: photoData } = await supabase
        .from("signature_book_photos")
        .select("id, photo_url, sort_order")
        .eq("signature_book_id", b.id)
        .order("sort_order");
      setPhotos((photoData as Photo[]) ?? []);

      setState("open");
    };
    load();
  }, [slug]);

  const handleSubmit = async (entry: GuestEntry) => {
    if (!book) return;
    const { error } = await supabase.from("signature_book_entries").insert({
      tenant_id:         book.tenant_id,
      signature_book_id: book.id,
      guest_name:        entry.guest_name,
      message:           entry.message || null,
      emoji:             entry.emoji || null,
      signature_data:    entry.signature_data || null,
      photo_id:          entry.photo_id || null,
      photo_url:         entry.photo_url || null,
      pos_x:             5,
      pos_y:             68,
      status:            "visible",
    });
    if (error) throw new Error(error.message);
    setLastGuestName(entry.guest_name);
    setState("thanks");
  };

  /* ─── Render ─────────────────────────────────────── */
  if (state === "loading") return <GuestBookLoading />;
  if (state === "error")   return <GuestBookError message={errorMsg} />;
  if (!book)               return <GuestBookError message="Error inesperado." />;

  if (state === "open") {
    return (
      <>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" />
        <GuestBookCover book={book} onEnter={() => setState("form")} />
      </>
    );
  }

  if (state === "thanks") {
    return (
      <>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&display=swap" rel="stylesheet" />
        <div
          className="min-h-screen flex items-center justify-center px-4 py-10"
          style={{ backgroundColor: "#FAF8F4" }}
        >
          <div className="w-full max-w-sm">
            <GuestBookThankYou
              guestName={lastGuestName}
              closingMessage={book.closing_message}
              accentColor={ACCENT}
              onBack={() => setState("form")}
            />
          </div>
        </div>
      </>
    );
  }

  /* form state */
  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" />

      <style>{`
        .gb-input:focus { outline: none; box-shadow: 0 0 0 2px ${ACCENT}40; border-color: ${ACCENT}60; }
      `}</style>

      <div
        className="min-h-screen py-8 px-4"
        style={{ backgroundColor: "#FAF8F4" }}
      >
        <div className="w-full max-w-sm mx-auto">

          {/* Header */}
          <div className="mb-6 text-center">
            <div
              className="w-12 h-12 flex items-center justify-center rounded-2xl mx-auto mb-3"
              style={{ backgroundColor: ACCENT + "20" }}
            >
              <i className="ri-book-open-line text-xl" style={{ color: ACCENT }}></i>
            </div>
            <h1
              className="text-2xl font-bold text-slate-800 leading-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              {book.title}
            </h1>
            {book.event_date && (
              <p className="text-slate-400 text-sm mt-1">
                {new Date(book.event_date).toLocaleDateString("es-MX", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
            )}
          </div>

          {/* Photos strip preview (if exist) */}
          {photos.length > 0 && (
            <div className="mb-5 -mx-1 flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
              {photos.slice(0, 6).map((p) => (
                <div key={p.id} className="w-16 h-16 shrink-0 rounded-xl overflow-hidden">
                  <img src={p.photo_url} alt="" className="w-full h-full object-cover object-top" />
                </div>
              ))}
              {photos.length > 6 && (
                <div className="w-16 h-16 shrink-0 rounded-xl bg-slate-200 flex items-center justify-center">
                  <span className="text-slate-500 text-xs font-semibold">+{photos.length - 6}</span>
                </div>
              )}
            </div>
          )}

          {/* Card */}
          <div className="bg-white rounded-3xl border border-slate-200 p-6 mb-4">
            <div className="mb-5">
              <h2 className="text-slate-800 font-bold text-base mb-1">Deja tu mensaje</h2>
              <p className="text-slate-400 text-xs">Tu mensaje quedará guardado para siempre en el libro</p>
            </div>
            <GuestBookForm
              photos={photos}
              accentColor={ACCENT}
              onSubmit={handleSubmit}
            />
          </div>

          {/* Footer */}
          <div className="text-center pb-4">
            <p className="text-xs text-slate-300">
              Creado con <span className="font-semibold">Pixory AI</span>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}